import { useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabase";

const GRID_SIZE = 40;

function GridBackground() {
  return (
    <svg
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        opacity: 0.15,
        pointerEvents: "none",
      }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern
          id="grid"
          width={GRID_SIZE}
          height={GRID_SIZE}
          patternUnits="userSpaceOnUse"
        >
          <path
            d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`}
            fill="none"
            stroke="#3B82F6"
            strokeWidth="0.5"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState(null);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Enter your email and password to continue.");
      return;
    }
    setLoading(true);
    try {
      // Step 1: authenticate the user
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) throw authError;

      // Step 2: check if the authenticated user exists in the admins table
      const { data: adminRecord, error: adminError } = await supabase
        .from("admins")
        .select("id, is_active")
        .eq("email", authData.user.email)
        .single();

      if (adminError || !adminRecord) {
        await supabase.auth.signOut();
        throw new Error("Access denied. This portal is restricted to admins only.");
      }

      // Step 3: reject inactive admins
      if (!adminRecord.is_active) {
        await supabase.auth.signOut();
        throw new Error("Your admin account has been deactivated. Contact support.");
      }

      navigate("/admin");
    } catch (err) {
      setError(err.message ?? "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const cardAccentActive = focusedField !== null;

  return (
    <div style={styles.root}>
      <GridBackground />

      {/* Radial glow behind card */}
      <div style={styles.glow} />

      <div style={styles.card}>
        {/* Accent bar */}
        <div
          style={{
            ...styles.accentBar,
            opacity: cardAccentActive ? 1 : 0.35,
            height: cardAccentActive ? "100%" : "40%",
          }}
        />

        <div style={styles.cardInner}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.iconWrap}>
              <ShieldIcon />
            </div>
            <div>
              <p style={styles.eyebrow}>Admin Console</p>
              <h1 style={styles.title}>Sign in</h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={styles.form} noValidate>
            {/* Email */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="admin-email">
                Email address
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField("email")}
                onBlur={() => setFocusedField(null)}
                placeholder="admin@yourplatform.com"
                style={{
                  ...styles.input,
                  borderColor:
                    focusedField === "email" ? "#3B82F6" : "rgba(255,255,255,0.1)",
                  boxShadow:
                    focusedField === "email"
                      ? "0 0 0 3px rgba(59,130,246,0.15)"
                      : "none",
                }}
                disabled={loading}
              />
            </div>

            {/* Password */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="admin-password">
                Password
              </label>
              <div style={styles.passwordWrap}>
                <input
                  id="admin-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="••••••••••••"
                  style={{
                    ...styles.input,
                    paddingRight: 44,
                    borderColor:
                      focusedField === "password"
                        ? "#3B82F6"
                        : "rgba(255,255,255,0.1)",
                    boxShadow:
                      focusedField === "password"
                        ? "0 0 0 3px rgba(59,130,246,0.15)"
                        : "none",
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.eyeBtn}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={styles.errorBox} role="alert">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitBtn,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? (
                <span style={styles.spinnerRow}>
                  <svg style={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                  </svg>
                  Verifying…
                </span>
              ) : (
                "Sign in to admin"
              )}
            </button>
          </form>

          <p style={styles.footer}>
            Access restricted to authorised personnel only.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    background: "#0D1117",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    position: "relative",
    overflow: "hidden",
    padding: "1rem",
  },
  glow: {
    position: "absolute",
    top: "30%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: 420,
    background: "#1C2333",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.07)",
    overflow: "hidden",
    zIndex: 1,
  },
  accentBar: {
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: 3,
    background: "linear-gradient(180deg, transparent, #3B82F6, transparent)",
    borderRadius: 2,
    transition: "height 0.4s ease, opacity 0.3s ease",
    pointerEvents: "none",
  },
  cardInner: {
    padding: "2.5rem 2rem 2rem",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: "2rem",
  },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "rgba(59,130,246,0.1)",
    border: "1px solid rgba(59,130,246,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#3B82F6",
    margin: 0,
    marginBottom: 2,
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: "#E2E8F0",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: "#94A3B8",
    letterSpacing: "0.02em",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    height: 44,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: "0 14px",
    fontSize: 14,
    color: "#E2E8F0",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
    caretColor: "#3B82F6",
  },
  passwordWrap: {
    position: "relative",
  },
  eyeBtn: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#64748B",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    lineHeight: 1,
    transition: "color 0.15s",
  },
  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    background: "rgba(248,113,113,0.08)",
    border: "1px solid rgba(248,113,113,0.2)",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    color: "#F87171",
    lineHeight: 1.4,
  },
  submitBtn: {
    width: "100%",
    height: 46,
    background: "#3B82F6",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: "0.01em",
    marginTop: 4,
    transition: "opacity 0.2s, transform 0.1s",
  },
  spinnerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  spinner: {
    width: 16,
    height: 16,
    animation: "spin 0.75s linear infinite",
  },
  footer: {
    marginTop: "1.75rem",
    fontSize: 11,
    color: "#475569",
    textAlign: "center",
    letterSpacing: "0.01em",
  },
};